import { createContext, useContext, useState, useEffect, useCallback, ReactNode, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { logger } from '../lib/productionLogger';
import { useAuthSessionRefresh } from '../hooks/useTabVisibility';
import { invalidateTenantQueries } from '../lib/queryClient';
import { useAuth } from './AuthContext';
import { FatalErrorScreen } from '../components/system/FatalErrorScreen';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Tenant {
  id: string;
  name: string;
  created_at: string;
  owner_id: string;
}

type TenantState = 'idle' | 'loading' | 'selected' | 'none' | 'error';
type BootstrapStatus = 'idle' | 'running' | 'done' | 'error';

interface TenantContextValue {
  tenant: Tenant | null;
  loading: boolean;
  isTenantLoading: boolean;
  error: string | null;
  tenantError: string | null;
  refreshTenant: () => Promise<void>;
  setTenant: (tenant: Tenant | null) => void;
  tenantState: TenantState;
  tenantId: string | null;
  retry: () => Promise<void>;
  bootstrapStatus: BootstrapStatus;
  bootstrapVersion: number;
}

const TenantContext = createContext<TenantContextValue | undefined>(undefined);

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Returns true for transient network / fetch failures that are safe to retry.
 * These should never permanently block workspace access.
 */
function isNetworkError(e: unknown): boolean {
  if (!e) return false;
  const msg = (e instanceof Error ? e.message : String((e as any)?.message ?? '')).toLowerCase();
  return (
    msg.includes('failed to fetch') ||   // Chrome / fetch API
    msg.includes('load failed') ||       // Safari
    msg.includes('networkerror') ||      // Firefox
    msg.includes('network error') ||
    msg.includes('err_internet') ||
    msg.includes('net::err') ||
    msg.includes('offline')
  );
}

const sleep = (ms: number) => new Promise<void>(r => setTimeout(r, ms));

/**
 * Retry a Supabase-style {data, error} query on transient network failures.
 * Stays in the same function call so the outer loading state is preserved.
 */
async function queryWithRetry<T>(
  fn: () => PromiseLike<{ data: T; error: any }>,
  maxAttempts = 3,
  baseDelayMs = 1200
): Promise<{ data: T; error: any }> {
  let result: { data: T; error: any } = { data: null as unknown as T, error: null };
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    result = await fn();
    if (!result.error || !isNetworkError(result.error)) return result;
    if (attempt < maxAttempts) {
      logger.log(`[TenantContext] Profile query network error – retrying (${attempt}/${maxAttempts})…`);
      await sleep(baseDelayMs * attempt);
    }
  }
  return result;
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export function TenantProvider({ children }: { children: ReactNode }) {
  const { authState, user } = useAuth();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bootstrapStatus, setBootstrapStatus] = useState<BootstrapStatus>('idle');
  const [bootstrapVersion, setBootstrapVersion] = useState(0);
  const loadingRef = useRef(false);
  const ensureWorkspacePromiseRef = useRef<Promise<void> | null>(null);
  const rpcAttemptedRef = useRef(false);

  const tenantState: TenantState = error
    ? 'error'
    : loading
    ? 'loading'
    : tenant
    ? 'selected'
    : 'none';

  const tenantId = tenant?.id || null;

  /**
   * loadTenant — loads or creates the tenant for the authenticated user.
   *
   * IMPORTANT: We deliberately do NOT call supabase.auth.getUser() here.
   * AuthContext already validated the session via getSession() (a local
   * read from storage — no network needed).  The user object it provides
   * contains the full JWT payload including user_metadata, so we can read
   * company_name / skip_tenant_creation directly without an extra server
   * round-trip.  That extra round-trip to /auth/v1/user was the source of
   * the transient "Workspace Error" crash on flaky connections.
   *
   * @param userId   - The authenticated user's UUID.
   * @param userMeta - user.user_metadata from the session JWT (no network needed).
   */
  const loadTenant = useCallback(async (
    userId: string,
    userMeta: Record<string, any> | null | undefined = {}
  ) => {
    if (loadingRef.current && ensureWorkspacePromiseRef.current) {
      logger.log('[TenantContext] Load already in progress, awaiting existing promise');
      return ensureWorkspacePromiseRef.current;
    }

    loadingRef.current = true;
    setLoading(true);
    setError(null);

    const loadPromise = (async () => {
      try {
        // ── Read metadata from session JWT — zero network calls ───────────────
        const meta = userMeta ?? {};
        const skipTenantCreation = meta.skip_tenant_creation;
        const companyName = meta.company_name || meta.companyName;

        if (import.meta.env.DEV) {
          logger.log('[TenantContext] User metadata (DEV):', {
            skip_tenant_creation: skipTenantCreation,
            company_name: companyName,
          });
        }

        // ── Profile check (retries on transient network failures) ─────────────
        const { data: existingProfile, error: profileCheckError } =
          await queryWithRetry(() =>
            supabase
              .from('profiles')
              .select('id, tenant_id, tenants(id, name, created_at, owner_id)')
              .eq('id', userId)
              .maybeSingle()
          );

        if (profileCheckError) {
          if (isNetworkError(profileCheckError)) {
            // All retries exhausted — connection still down
            throw new Error(
              'Network unavailable. Please check your connection and try again.'
            );
          }
          // Non-network DB error — treat as "profile not found" so bootstrap can create it
          logger.warn('[TenantContext] Profile check error (treating as missing):', profileCheckError.message);
        }

        // ── CASE 1: Profile exists with tenant — fast path ────────────────────
        if (existingProfile?.tenant_id) {
          const tenantInfo = Array.isArray(existingProfile.tenants)
            ? existingProfile.tenants[0]
            : existingProfile.tenants;

          if (!tenantInfo) {
            throw new Error('Workspace data not found. Please contact support.');
          }

          setTenant({
            id: existingProfile.tenant_id,
            name: tenantInfo.name || 'My Workspace',
            created_at: tenantInfo.created_at || new Date().toISOString(),
            owner_id: tenantInfo.owner_id || userId,
          });
          return;
        }

        // ── CASE 2: Invited user — no RPC allowed ─────────────────────────────
        if (skipTenantCreation) {
          throw new Error(
            'Your account is not properly set up. Please accept your invitation link again or contact your administrator.'
          );
        }

        // ── CASE 3: Regular new user — create workspace via RPC ───────────────

        if (rpcAttemptedRef.current) {
          throw new Error('Workspace initialization already attempted. Please sign out and try again.');
        }

        const hasCompanyName =
          companyName != null &&
          typeof companyName === 'string' &&
          companyName.trim().length > 0;

        if (!hasCompanyName) {
          rpcAttemptedRef.current = true;
          throw new Error(
            'Company name missing from user metadata. Please sign up again and enter your company name.'
          );
        }

        const trimmedCompanyName = companyName.trim();
        const isGenericPlaceholder = [
          'workspace', 'default', 'default workspace', 'my workspace',
        ].includes(trimmedCompanyName.toLowerCase());

        if (isGenericPlaceholder) {
          rpcAttemptedRef.current = true;
          throw new Error(
            'Invalid company name format. Please sign out and sign up again with your actual company name.'
          );
        }

        if (existingProfile?.tenant_id) {
          throw new Error('Profile already has a workspace. Please refresh the page.');
        }

        logger.log('[TenantContext] Calling ensure_profile_and_tenant…', {
          companyName: trimmedCompanyName,
          userId,
        });

        rpcAttemptedRef.current = true;
        setBootstrapStatus('running');

        const rpcPromise = supabase.rpc('ensure_profile_and_tenant');
        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(
            () => reject(new Error('Workspace initialisation timed out. Please refresh the page.')),
            15_000
          )
        );
        const { data: rpcData, error: rpcError } =
          await Promise.race([rpcPromise, timeoutPromise]) as Awaited<typeof rpcPromise>;

        if (rpcError) {
          if (rpcError.message?.includes('Company name missing')) {
            throw new Error('Company name missing from user metadata. Please sign up again and enter your company name.');
          }
          if (rpcError.message?.includes('Invalid company name format')) {
            throw new Error('Invalid company name format. Please sign up again with your actual company name.');
          }
          throw new Error(`Failed to create workspace: ${rpcError.message || 'Unknown error'}`);
        }

        if (!rpcData || rpcData.length === 0) {
          throw new Error('Failed to initialize workspace. Please try again or contact support.');
        }

        const profileData = rpcData[0];
        if (!profileData.tenant_id) {
          throw new Error('Workspace creation failed. Please contact support.');
        }

        setTenant({
          id: profileData.tenant_id,
          name: profileData.tenant_name || 'My Workspace',
          created_at: new Date().toISOString(),
          owner_id: userId,
        });

        setBootstrapStatus('done');
        setBootstrapVersion(prev => prev + 1);
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Failed to load workspace';
        logger.warn('[TenantContext] Error loading tenant:', msg);
        setError(msg);
        setTenant(null);
        setBootstrapStatus('error');
      } finally {
        setLoading(false);
        loadingRef.current = false;
        ensureWorkspacePromiseRef.current = null;
      }
    })();

    ensureWorkspacePromiseRef.current = loadPromise;
    return loadPromise;
  }, []);

  const refreshTenant = useCallback(async () => {
    logger.log('[TenantContext] Refreshing tenant…');
    if (!user?.id) return;

    // If a tenant is already selected, do a silent background check to avoid
    // blanking the page. Only fall back to a full reload if the tenant has
    // changed or disappeared (edge case: account transferred, tenant deleted).
    if (tenant?.id) {
      try {
        const { data } = await supabase
          .from('profiles')
          .select('tenant_id')
          .eq('id', user.id)
          .maybeSingle();

        if (data?.tenant_id && data.tenant_id === tenant.id) {
          // Still the same tenant — nothing to do beyond query invalidation,
          // which the caller (useAuthSessionRefresh) handles.
          logger.log('[TenantContext] Silent refresh: tenant unchanged');
          return;
        }
      } catch {
        // Network error during silent check — keep existing tenant, don't flash.
        logger.log('[TenantContext] Silent refresh failed — keeping existing tenant');
        return;
      }
      // Tenant changed or profile lost its tenant_id — do a full reload.
      logger.log('[TenantContext] Silent refresh: tenant changed, doing full reload');
    }

    rpcAttemptedRef.current = false;
    setBootstrapStatus('idle');
    await loadTenant(user.id, user.user_metadata ?? {});
  }, [loadTenant, user, tenant]);

  // ── Load tenant when authenticated ────────────────────────────────────────

  useEffect(() => {
    if (authState === 'signedIn' && user?.id) {
      loadTenant(user.id, user.user_metadata ?? {});
    } else if (authState === 'signedOut') {
      setTenant(null);
      setLoading(false);
      rpcAttemptedRef.current = false;
      setBootstrapStatus('idle');
      setBootstrapVersion(0);
    }
  }, [authState, user, loadTenant]);

  // Reset single-run guard on identity change so a fresh sign-in always
  // gets a clean bootstrap attempt.
  useEffect(() => {
    rpcAttemptedRef.current = false;
  }, [user?.id]);

  // Refresh tenant + invalidate queries when session is refreshed
  // (e.g. tab becomes visible after a long background period).
  useAuthSessionRefresh(useCallback(() => {
    if (tenant?.id) invalidateTenantQueries(tenant.id);
    refreshTenant();
  }, [tenant, refreshTenant]));

  // ── Fatal error check ─────────────────────────────────────────────────────
  // These errors are genuine config problems, not transient failures.

  const isFatalError = error && (
    error.includes('Company name is required') ||
    error.includes('Company name missing') ||
    error.includes('does not have a company name') ||
    error.includes('Invalid company name format') ||
    error.includes('not properly set up')
  );

  const handleSignOut = useCallback(async () => {
    try {
      await supabase.auth.signOut();
      setTenant(null);
      setError(null);
      setLoading(false);
      setBootstrapStatus('idle');
      setBootstrapVersion(0);
      rpcAttemptedRef.current = false;
    } catch (e) {
      logger.warn('[TenantContext] Error signing out:', e);
    }
  }, []);

  if (isFatalError && authState === 'signedIn') {
    return <FatalErrorScreen error={error} onSignOut={handleSignOut} />;
  }

  return (
    <TenantContext.Provider
      value={{
        tenant,
        loading,
        isTenantLoading: loading,
        error,
        tenantError: error,
        refreshTenant,
        setTenant,
        tenantState,
        tenantId,
        retry: refreshTenant,
        bootstrapStatus,
        bootstrapVersion,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within TenantProvider');
  }
  return context;
}

export function useCurrentTenantId(): string | null {
  const { tenant } = useTenant();
  return tenant?.id || null;
}
